import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { contains, equals, values } from 'ramda';
import { compareUsers } from '../../helpers/ArrayUtils';
import { buildRegex } from '../../helpers/TextUtils';

import User from '../User/User';

import classnames from 'classnames';

export default class UserDropdown extends Component {

  static propTypes = {
    className: PropTypes.string,         // CSS class for the component
    qaId: PropTypes.string,              // string that functions similar to an ID for QA to find the element with ease.
    identityId: PropTypes.number,        // the user ID of the identity user
    ignoreTab: PropTypes.bool,           // if true then the tab key will have default functionality and will not function as enter
    onHide: PropTypes.func,              // function callback when the user dropdown is hidden
    onSelect: PropTypes.func.isRequired, // function callback when a user is selected
    noResultsText: PropTypes.string,     // string to override the default no result text
    show: PropTypes.bool,                // if true then the dropdown is visible
    style: PropTypes.object,             // object representation of CSS styles for the component
    text: PropTypes.string.isRequired,   // the value of the input box used to filter the users in the dropdown
    users: PropTypes.object,             // all users from redux state in object format of userID -> user
    usersToExclude: PropTypes.array,     // list of user IDs that should be excluded from the list of users
  };

  constructor(props) {
    super(props);
    this.userDropdown = React.createRef();
    this.state = this.buildCurrentState(props);
  }

  componentDidMount() {
    if (this.props.show) {
      document.addEventListener('keydown', this.handleKeyboardEvents);
    }
  }

  componentWillUnmount() {
    if (this.props.show) {
      document.removeEventListener('keydown', this.handleKeyboardEvents);
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.show && this.props.show) {
      document.addEventListener('keydown', this.handleKeyboardEvents);
      this.setState(this.buildCurrentState(this.props));
      this.scrollToTop();
    }

    if (prevProps.show && !this.props.show) {
      document.removeEventListener('keydown', this.handleKeyboardEvents);
    }

    if (prevProps.text !== this.props.text || !equals(prevProps.usersToExclude, this.props.usersToExclude) || !equals(prevProps.users, this.props.users)) {
      this.setState(this.buildCurrentState(this.props));
      this.scrollToTop();
    }
  }

  stopFurtherAction = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  buildCurrentState = props => {

    const matchingUserIds = this.getMatchingUserIds(props.text);

    return {
      matchingUserIds: props.show ? matchingUserIds : [],
      selectedMatchPos: 0,
    }
  };

  /**
   * Gets a list of matching user IDs where the provided text is contained in
   * the first name, last name, or the mention name. If the provided text string
   * is empty, then all user IDs are returned.
   */
  getMatchingUserIds = (text) => {
    const usersList = this.props.usersToExclude ? values(this.props.users).filter(u => !contains(u.id, this.props.usersToExclude)) : values(this.props.users);
    if (!text) {
      return usersList
        .sort(compareUsers)
        .map(u => u.id);
    }

    const textRegex = buildRegex(text);
    return usersList
      .filter(u => textRegex.test(u.mentionName) || textRegex.test(u.firstName) || textRegex.test(u.lastName) || textRegex.test(`${u.firstName} ${u.lastName}`))
      .sort(compareUsers)
      .map(u => u.id);
  };

  /**
   * Handles keyboard events.
   * event.which and event.keyCode properties are deprecated and will be removed from the web standard (see https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/which).
   * event.code returns a string representation of the keypressed and is the new web standard, but not all browsers have it implemented so which and keycode are available as a fallback.
   * @param event a keyboard event
   */
  handleKeyboardEvents = event => {
    const key = event.code || event.which || event.keyCode;

    // SC-476 - The fallthrough for tabs to the enter is intentional if not ignoring tabs
    // noinspection FallThroughInSwitchStatementJS
    switch (key) {
      case 'Tab':
      case 9:
        if (this.props.ignoreTab) break;
      // eslint-disable-next-line
      case 'Enter':
      case 13:
        this.stopFurtherAction(event);
        this.selectUser(event);
        break;
      case 'Escape':
      case 27:
        this.stopFurtherAction(event);
        this.props.onHide && this.props.onHide(event);
        break;
      case 'ArrowUp':
      case 38:
        this.stopFurtherAction(event);
        if (!this.state.matchingUserIds || this.state.matchingUserIds.length < 2) break;
        if (this.state.selectedMatchPos > 0) this.updateSelected(this.state.selectedMatchPos - 1);
        else this.updateSelected(this.state.matchingUserIds.length - 1);
        this.scrollToSelected();
        break;
      case 'ArrowDown':
      case 40:
        this.stopFurtherAction(event);
        if (!this.state.matchingUserIds || this.state.matchingUserIds.length < 2) break;
        if (this.state.selectedMatchPos < this.state.matchingUserIds.length - 1) this.updateSelected(this.state.selectedMatchPos + 1);
        else this.updateSelected(0);
        this.scrollToSelected();
        break;
      default:
        break;
    }

  };

  /**
   * Updates the state to select the a matching user given the position in the list.
   */
  updateSelected = pos => {
    this.setState({ selectedMatchPos: pos });
  };

  /**
   * Scrolls to the currently selected user in the list.
   * Things to note:
   *  1: This needs be called after a setState
   *  2: Relies on a the ref 'userDropdown' which is the list users
   *  3: Should not be used with the mouse events
   */
  scrollToSelected = () => {
    this.userDropdown.current // gets the reference to the user dropdown
      .getElementsByClassName("selected")[0] // gets the matching list element that has the class 'selected'
      .scrollIntoView({ // the scroll options to only scroll if the selected element is not visible
        block: "nearest",
        inline: "nearest"
      });
  };

  /**
   * Scrolls to the top of the user dropdown.
   */
  scrollToTop = () => this.userDropdown.current.scrollTop = 0;

  /**
   * Executes the provided onSelect callback function with the selected userId.
   * @param event The javascript event (either a click or keyboard event)
   * @param index The index of the user selected. If not provided, the index is found from state.
   */
  selectUser = (event, index) => {
    const pos = index || this.state.selectedMatchPos;
    const selectedUserId = this.state.matchingUserIds[pos];
    selectedUserId && this.props.onSelect(selectedUserId, event);
  };

  /**
   * Important note: onMouseDown and onTouchStart call stopFurtherAction because those two events
   * are triggered before onBlur. This allows the select to happen and stop the closing of dropdown
   * when the input is blurred. Click events are triggered after blur events.
   */
  render() {
    const { className, qaId, identityId, users, show, style, noResultsText } = this.props;
    const { matchingUserIds, selectedMatchPos } = this.state;

    return (
      <div
        className={classnames({
          "hidden": !show,
          [className]: !!className,
          "user-dropdown-container": true,
        })}
        style={style}
      >
        <ul ref={this.userDropdown} className="user-dropdown-list" data-qa={qaId}>
          {matchingUserIds.map((userId, index) =>
            <li
              onMouseOver={() => this.updateSelected(index)}
              onMouseDown={e => e.preventDefault()}
              onTouchStart={e => e.preventDefault()}
              onClick={e => this.selectUser(e, index)}
              className={classnames({
                "user-list-item": true,
                "selected": index === selectedMatchPos,
              })}
              key={userId}>
              <User
                {...users[userId]}
                isCurrentUser={identityId && userId === identityId}
              />
            </li>
          )}
          {matchingUserIds.length === 0 &&
            <li className="user-list-item">
              <div className="no-results">
                {noResultsText || 'No results found'}
              </div>
            </li>
          }
        </ul>
      </div>
    );
  }
}
